import { ChangeDetectionStrategy, Component, DestroyRef, HostListener, Inject, Input, LOCALE_ID, OnInit } from '@angular/core';
import { UntypedFormGroup, FormBuilder, Validators, AbstractControl } from '@angular/forms';
import {
  selectAdminCustomerSpace,
  selectCreatedUser,
  selectNavigatedCustomerSpace,
  selectUserOrganizationUnits,
} from '@core/core.selectors';
import { CustomerSpaceAdmin, CustomerSpaceUser, Role, RoleName, User, getUserDisplayName } from '@core/core.state';
import { PostUserDto } from '@core/users/user.model';
import { Store, select } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { toDateOnly } from '@platform/shared/date-only';
import { validPhoneNumber } from '@platform/shared/utilities/phonenumber-validator';
import * as RouterActions from '@platform/router.actions';
import { Error, ResourceState, ResourceStatuses, filterSucceededResource } from '@platform/shared/resource.state';
import {
  EmailValidationPattern,
  OrganizationUnitDisallowedCharacters,
  OrganizationUnitValidationPattern,
} from '@platform/shared/utilities/validation-utility';
import * as CoreActions from '@core/core.actions';
import { RootState } from '@root/app.state';
import { parsePhoneNumberWithError as parsePhoneNumber } from 'libphonenumber-js';
import { Observable, startWith, map, filter, combineLatest } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CustomerSpaceApp, getPermissionForApp } from '@core/customer-space-admin/app-permission-helper';
import { requireSelection } from '@platform/shared/utilities/require-selection-validator';
import { PostCustomerSpaceUserDto } from '@core/customer-space/customer-space.model';
import { OptionItem } from '@platform/components/forms/forms.utils';

@Component({
  selector: 'app-create-user',
  templateUrl: './create-user.component.html',
  styleUrls: ['./create-user.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateUserComponent implements OnInit {
  @Input() customerSpaceId = '';
  @Input() showPermissions = true;
  public userForm: UntypedFormGroup;
  public savePending$!: Observable<boolean>;
  public filteredOrganizationUnits$: Observable<OptionItem[]>;

  // from 'update-employee-dialog.component'
  public appPermissionsForm!: UntypedFormGroup;
  public updateEmployeeForm!: UntypedFormGroup;
  public isInternalUser!: boolean;
  public userId: string | null = null;
  public roles: Role[] = [];
  public isEditMode: boolean;
  public hasAbacusInstanceAndMandator!: boolean;
  public filteredUsers$!: Observable<User[]>;

  randomName1 = Math.random().toString();
  randomName2 = Math.random().toString();
  randomName3 = Math.random().toString();
  randomName4 = Math.random().toString();
  randomName5 = Math.random().toString();
  randomName6 = Math.random().toString();

  private organizationUnits: OptionItem[] = [];

  // from 'update-...'
  // eslint-disable-next-line @typescript-eslint/member-ordering
  public customerSpaceRoles: Role[];
  private activeUsers: User[]; // todo remove
  private abacusMandatorId!: string;

  constructor(
    private store$: Store<RootState>,
    private translate: TranslateService,
    private formBuilder: FormBuilder,
    @Inject(LOCALE_ID) private locale: string,
    private destroyRef: DestroyRef,
  ) {
    // this.activeUsers = data.activeUsers;
    this.activeUsers = [];
    // this.customerSpaceRoles = data.customerSpaceRoles;
    this.customerSpaceRoles = [];
    this.isEditMode = false;
    // this.customerSpaceId = data.customerSpaceId; // should get from route

    this.userForm = formBuilder.group({
      firstName: ['', [Validators.required, Validators.maxLength(200)]],
      lastName: ['', [Validators.required, Validators.maxLength(200)]],
      organizationUnit: ['', [Validators.required, Validators.maxLength(200), Validators.pattern(OrganizationUnitValidationPattern)]],
      mobileNumber: ['', [validPhoneNumber(), Validators.required]],
      mail: ['', [Validators.required, Validators.maxLength(200), Validators.pattern(EmailValidationPattern)]],
      language: ['German', [Validators.required]],
      activeUntil: formBuilder.control<Date | null>(null),
    });

    this.setupEmployeeForm(null);
    this.setupAppPermissionForm();

    // todo remove temp values
    /*
    this.userForm.patchValue({
      firstName: 'Some first2',
      lastName: 'Some last2',
      mail: 'maxilyukhindev@gmail.com',
      mobileNumber: '345622543',
    });
    */
  }

  get organisationValidationRules() {
    return {
      pattern: (t: TranslateService) => t.instant('users.user-form.error.invalid-format') + OrganizationUnitDisallowedCharacters,
    };
  }

  get mobileNumberValidationRules() {
    return {
      invalidPhoneNumber: (t: TranslateService) => t.instant('users.user-form.error.mobile-number-invalid'),
    };
  }

  get mailValidationRules() {
    return {
      pattern: 'users.user-form.error.mail-invalid-format',
      backendError: () => this.userForm.get('mail')!.getError('backendError'),
    };
  }

  @HostListener('document:keydown.enter', ['$event'])
  enter($event: Event) {
    $event.preventDefault();
    $event.stopPropagation();
    this.saveUser();
  }

  ngOnInit(): void {
    this.savePending$ = this.store$.pipe(
      select(selectCreatedUser),
      map((state) => state.status === ResourceStatuses.PENDING),
    );

    this.store$
      .pipe(
        select(selectNavigatedCustomerSpace),
        filter((x) => x.data !== null),
        map((x) => x.data),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((x) => {
        this.hasAbacusInstanceAndMandator =
          x.abacusMandator && Boolean(x.abacusMandator.abacusInstanceId) && Boolean(x.abacusMandator.abacusMandatorId);

        if (this.hasAbacusInstanceAndMandator) {
          this.abacusMandatorId = x.abacusMandator.abacusMandatorId;

          this.appPermissionsForm.get('payroll')?.enable();
          this.appPermissionsForm.get('creditor')?.enable();
        } else {
          this.updateEmployeeForm.get('isMailSent')!.disable();
          this.updateEmployeeForm.get('isMailSent')!.setValue(false);
          this.appPermissionsForm.get('linksAbacus')!.disable();
          this.appPermissionsForm.get('linksAbacus')!.setValue(false);
          this.appPermissionsForm.get('linksAbacusErp')!.disable();
          this.appPermissionsForm.get('linksAbacusErp')!.setValue(false);

          this.appPermissionsForm.get('payroll')?.disable();
          this.appPermissionsForm.get('creditor')?.disable();
        }
      });

    this.store$.pipe(select(selectCreatedUser), takeUntilDestroyed(this.destroyRef)).subscribe((state) => this.onCreatedUserLoaded(state));

    combineLatest([
      this.store$.pipe(select(selectUserOrganizationUnits), filterSucceededResource()),
      this.store$.pipe(select(selectNavigatedCustomerSpace), filterSucceededResource()),
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([organizations, customerSpace]) => {
        console.log({ organizations, name: customerSpace.name });
        this.customerSpaceId = customerSpace.id;
        this.organizationUnits = organizations.map((org) => ({ value: org, label: org }));
        if (!this.organizationUnits.find((e) => e.value === customerSpace.name)) {
          this.organizationUnits.push({
            value: customerSpace.name,
            label: customerSpace.name,
          });
        }

        this.userForm.get('organizationUnit')?.patchValue(customerSpace.name);

        this.filteredOrganizationUnits$ = this.userForm.get('organizationUnit')!.valueChanges.pipe(
          startWith(''),
          map((val) => this.filterOrganizationUnits(val)),
        );
      });

    this.store$
      .pipe(select(selectAdminCustomerSpace), takeUntilDestroyed(this.destroyRef))
      .subscribe((state) => this.onCustomerSpaceLoaded(state));
  }

  public getDisplayName(user: User): string {
    return getUserDisplayName(user);
  }

  public getTranslatedRoleName(name: RoleName): string {
    if (!name) {
      return null!;
    }

    return this.translate.instant(`roles.${name.toLocaleLowerCase()}`);
  }

  public saveUser() {
    if (!this.userForm.valid) {
      return;
    }

    const phoneNumber = parsePhoneNumber(this.userForm.get('mobileNumber')!.value, 'CH');

    const user: PostUserDto = {
      firstName: this.userForm.get('firstName')!.value,
      lastName: this.userForm.get('lastName')!.value,
      organizationUnit: this.userForm.get('organizationUnit')!.value,
      mail: this.userForm.get('mail')!.value,
      mobileNumber: phoneNumber.number,
      language: this.userForm.get('language')!.value,
      activeUntil: toDateOnly(this.userForm.get('activeUntil')!.value),
    };

    const data = {
      // selectedEmployee: this.employee().value,
      selectedRoleId: this.memberRoleId()!,
      selectedIsContact: this.isContact().value,
      selectedIsMailSent: this.isMailSent().value,
      selectedRoleDescription: this.roleDescription().value,
      appPermissions: this.getSelectedAppPermissions(),
      abacusMandatorId: this.abacusMandatorId,
    };

    const updateUserDto: PostCustomerSpaceUserDto = {
      roleId: data.selectedRoleId,
      userId: '',
      isContact: data.selectedIsContact,
      isMailSent: data.selectedIsMailSent,
      roleDescription: data.selectedRoleDescription,
      appPermissions: data.appPermissions,
      abacusMandatorId: data.abacusMandatorId,
    };

    const createUserAction = this.showPermissions
      ? CoreActions.createUser({ user, update: updateUserDto, customerSpaceId: this.customerSpaceId })
      : CoreActions.createUser({ user });

    this.store$.dispatch(createUserAction);
    return;
  }

  public addOrUpdateEmployee(data: any): void {
    if (!data) {
      return;
    }

    this.store$.dispatch(
      CoreActions.updateCustomerSpaceUser({
        customerSpaceId: this.customerSpaceId,
        updateUser: {
          roleId: data.selectedRoleId,
          userId: data.selectedEmployee.id,
          isContact: data.selectedIsContact,
          isMailSent: data.selectedIsMailSent,
          roleDescription: data.selectedRoleDescription,
          appPermissions: data.appPermissions,
          abacusMandatorId: this.abacusMandatorId,
        },
      }),
    );
  }

  public navigateBack() {
    this.store$.dispatch(RouterActions.back());
  }

  public getOrgUnitDisallowedCharactersValidationMessage(): string {
    return this.translate.instant('users.user-form.error.invalid-format') + OrganizationUnitDisallowedCharacters;
  }

  // from
  public doesUserHaveAppPermission(user: CustomerSpaceUser, app: CustomerSpaceApp) {
    const appPermission = getPermissionForApp(app, this.customerSpaceId);

    return user.appPermissions.indexOf(appPermission) >= 0;
  }

  public doesUserHaveAppPermissionLocked(user: CustomerSpaceUser, app: CustomerSpaceApp) {
    const appPermission = getPermissionForApp(app, this.customerSpaceId);

    return user.lockedAppPermissions && user.lockedAppPermissions.indexOf(appPermission) >= 0;
  }

  public getUserDisplayName(user: User): string {
    return getUserDisplayName(user);
  }

  private onCustomerSpaceLoaded(state: ResourceState<CustomerSpaceAdmin>) {
    if (state.data) {
      if (state.status === ResourceStatuses.SUCCEEDED) {
        console.log('Setting roles', state.data.roles);
        this.customerSpaceRoles = state.data.roles;
        this.roles = state.data.roles;
      }
    }
  }

  private filterOrganizationUnits(val: string | null) {
    if (typeof val !== 'string') {
      return this.organizationUnits.slice();
    }

    const vallower = val.toLowerCase();

    return this.organizationUnits.filter((organizationUnit) => organizationUnit.value.toLowerCase().includes(vallower));
  }

  private onCreatedUserLoaded(state: ResourceState<User>) {
    if (state.data) {
      if (state.status === ResourceStatuses.SUCCEEDED) {
        this.navigateBack();
      }
    }

    if (state.status === ResourceStatuses.FAILED) {
      if (state.error?.errors) {
        this.setFormErrors(state.error?.errors);
      }
    }
  }

  private setFormErrors(errors: Error) {
    if (errors['Mail']) {
      this.userForm.get('mail')!.setErrors({
        backendError: errors['Mail'][0],
      });
    }
  }

  //
  private setupAppPermissionForm() {
    this.appPermissionsForm = this.formBuilder.group({
      documents: [false],
      tasks: [false],
      payroll: [false],
      creditor: [false],
      projects: [false],
      financialStatementWorkings: [false],
      audit: [false],
      taxDeclaration: [false],
      links: [false],
      linksAbacus: [false],
      linksAbacusErp: [false],
      linksAbacusObtErp: [false],
      linksTicketPortal: [false],
      linksObtWebsite: [false],
    });

    if (!!this.employee().value) {
      const selectedEmployee = this.employee().value;

      if (selectedEmployee.customerSpaceRole?.name === 'CustomerSpaceAdmin') {
        this.setAppPermissionFormForAdmin();
      } else {
        this.appPermissionsForm.get('documents')!.setValue(this.doesUserHaveAppPermission(selectedEmployee, 'DOCUMENTS'));
        this.appPermissionsForm.get('tasks')!.setValue(this.doesUserHaveAppPermission(selectedEmployee, 'TASKS'));
        this.appPermissionsForm.get('payroll')!.setValue(this.doesUserHaveAppPermission(selectedEmployee, 'PAYROLL'));
        this.appPermissionsForm.get('creditor')!.setValue(this.doesUserHaveAppPermission(selectedEmployee, 'CREDITOR'));
        this.appPermissionsForm.get('projects')!.setValue(this.doesUserHaveAppPermission(selectedEmployee, 'PROJECTS'));
        this.appPermissionsForm
          .get('financialStatementWorkings')!
          .setValue(this.doesUserHaveAppPermission(selectedEmployee, 'FILE_PROCESS_FINANCIAL_STATEMENT_WORKINGS'));
        this.appPermissionsForm.get('audit')!.setValue(this.doesUserHaveAppPermission(selectedEmployee, 'FILE_PROCESS_AUDIT'));
        this.appPermissionsForm
          .get('taxDeclaration')
          ?.setValue(this.doesUserHaveAppPermission(selectedEmployee, 'FILE_PROCESS_TAX_DECLARATION'));
        this.appPermissionsForm.get('links')!.setValue(this.doesUserHaveAppPermission(selectedEmployee, 'LINKS'));
        this.appPermissionsForm.get('linksAbacus')!.setValue(this.doesUserHaveAppPermission(selectedEmployee, 'LINKS_ABACUS'));
        this.appPermissionsForm.get('linksAbacusErp')!.setValue(this.doesUserHaveAppPermission(selectedEmployee, 'LINKS_ABACUS_ERP'));
        this.appPermissionsForm
          .get('linksAbacusObtErp')!
          .setValue(this.doesUserHaveAppPermission(selectedEmployee, 'LINKS_ABACUS_OBT_ERP'));
        this.appPermissionsForm.get('linksTicketPortal')!.setValue(this.doesUserHaveAppPermission(selectedEmployee, 'LINKS_TICKET_PORTAL'));
        this.appPermissionsForm.get('linksObtWebsite')!.setValue(this.doesUserHaveAppPermission(selectedEmployee, 'LINKS_OBT_WEBSITE'));

        this.lockAppPermissionsForUser(selectedEmployee);
      }
    } else {
      this.resetAppPermissionFormToDefaults();
    }

    this.appPermissionsForm.get('links')!.valueChanges.subscribe((v) => {
      if (!v) {
        this.appPermissionsForm.get('linksAbacus')!.disable();
        this.appPermissionsForm.get('linksAbacusErp')!.disable();
        this.appPermissionsForm.get('linksAbacusObtErp')!.disable();
        this.appPermissionsForm.get('linksTicketPortal')!.disable();
        this.appPermissionsForm.get('linksObtWebsite')!.disable();
      } else {
        this.appPermissionsForm.get('linksAbacus')!.enable();
        this.appPermissionsForm.get('linksAbacusErp')!.enable();
        this.appPermissionsForm.get('linksAbacusObtErp')!.enable();
        this.appPermissionsForm.get('linksTicketPortal')!.enable();
        this.appPermissionsForm.get('linksObtWebsite')!.enable();
      }
    });
  }

  private lockAppPermissionsForUser(selectedEmployee: CustomerSpaceUser) {
    if (!selectedEmployee) {
      this.appPermissionsForm.get('projects')!.enable();
      return;
    }

    if (this.doesUserHaveAppPermissionLocked(selectedEmployee, 'PROJECTS')) {
      this.appPermissionsForm.get('projects')!.setValue(true);
      this.appPermissionsForm.get('projects')!.disable();
    } else {
      this.appPermissionsForm.get('projects')!.enable();
    }
  }

  private resetAppPermissionFormToDefaults() {
    this.appPermissionsForm.get('documents')!.setValue(true);
    this.appPermissionsForm.get('tasks')!.setValue(true);
    this.appPermissionsForm.get('payroll')!.setValue(false);
    this.appPermissionsForm.get('creditor')!.setValue(false);
    this.appPermissionsForm.get('projects')!.setValue(false);
    this.appPermissionsForm.get('financialStatementWorkings')!.setValue(false);
    this.appPermissionsForm.get('taxDeclaration')!.setValue(false);
    this.appPermissionsForm.get('audit')!.setValue(false);
    this.appPermissionsForm.get('links')!.setValue(true);
    // this.appPermissionsForm.get('linksAbacus')!.setValue(true);
    // this.appPermissionsForm.get('linksAbacusErp')!.setValue(true);
    this.appPermissionsForm.get('linksAbacusObtErp')!.setValue(true);
    this.appPermissionsForm.get('linksTicketPortal')!.setValue(true);
    this.appPermissionsForm.get('linksObtWebsite')!.setValue(true);

    this.appPermissionsForm.enable();
  }

  private setAppPermissionFormForAdmin() {
    this.appPermissionsForm.get('documents')!.setValue(true);
    this.appPermissionsForm.get('tasks')!.setValue(true);
    this.appPermissionsForm.get('payroll')!.setValue(true);
    this.appPermissionsForm.get('creditor')!.setValue(true);
    this.appPermissionsForm.get('projects')!.setValue(true);
    this.appPermissionsForm.get('financialStatementWorkings')!.setValue(true);
    this.appPermissionsForm.get('taxDeclaration')!.setValue(true);
    this.appPermissionsForm.get('audit')!.setValue(true);
    this.appPermissionsForm.get('links')!.setValue(true);
    this.appPermissionsForm.get('linksAbacus')!.setValue(true);
    this.appPermissionsForm.get('linksAbacusErp')!.setValue(true);
    this.appPermissionsForm.get('linksAbacusObtErp')!.setValue(true);
    this.appPermissionsForm.get('linksTicketPortal')!.setValue(true);
    this.appPermissionsForm.get('linksObtWebsite')!.setValue(true);

    this.appPermissionsForm.disable();
  }

  private setupEmployeeForm(selectedEmployee: CustomerSpaceUser | null) {
    this.updateEmployeeForm = this.formBuilder.group({
      employee: [selectedEmployee, [Validators.required, requireSelection]],
      roleId: [selectedEmployee?.customerSpaceRole?.id, []],
      isContact: [selectedEmployee?.isContact, null],
      isMailSent: [selectedEmployee?.isMailSent, null],
      roleDescription: [selectedEmployee?.roleDescription, null],
    });

    // console.log(this.customerSpaceRoles);
    this.roles = this.customerSpaceRoles;
    // if (!!selectedEmployee) {
    //   this.userId = selectedEmployee.id;
    //   this.isInternalUser = selectedEmployee.isInternal;
    //   this.employee().disable();
    //   this.roles = this.customerSpaceRoles.filter((r) => !r.isRestrictedToInternalUsers || selectedEmployee.isInternal);
    // } else {
    //   this.roleId().disable();
    //   this.userId = null;
    // }

    this.filteredUsers$ = this.employee().valueChanges.pipe(
      startWith(''),
      map((val) => this.filterUsers(val)),
    );

    this.roleId().valueChanges.subscribe((id) => {
      if (!id) {
        return;
      }

      const role = this.roles.find((r) => r.id === id);

      if (role?.name === 'CustomerSpaceAdmin') {
        this.setAppPermissionFormForAdmin();
      } else {
        this.resetAppPermissionFormToDefaults();
        this.lockAppPermissionsForUser(this.employee().value);
      }

      if (!this.hasAbacusInstanceAndMandator) {
        this.appPermissionsForm.get('payroll')!.setValue(false);
        this.appPermissionsForm.get('creditor')!.setValue(false);
        this.appPermissionsForm.get('payroll')!.disable();
        this.appPermissionsForm.get('creditor')!.disable();
      }
    });
  }

  private memberRoleId() {
    const role = this.customerSpaceRoles.find((r) => r.name === 'Employee');
    if (!role) {
      alert('Member role was not found');
    }
    return role?.id;
  }

  private employee(): AbstractControl {
    return this.updateEmployeeForm.get('employee')!;
  }

  private roleId(): AbstractControl {
    return this.updateEmployeeForm.get('roleId')!;
  }

  private isContact(): AbstractControl {
    return this.updateEmployeeForm.get('isContact')!;
  }

  private isMailSent(): AbstractControl {
    return this.updateEmployeeForm.get('isMailSent')!;
  }

  private roleDescription(): AbstractControl {
    return this.updateEmployeeForm.get('roleDescription')!;
  }

  private filterUsers(val: string): User[] {
    if (typeof val !== 'string') {
      return [];
    }

    return this.activeUsers.filter((user) => this.getUserDisplayName(user).toLowerCase().includes(val.toLowerCase()));
  }

  private getSelectedAppPermissions(): string[] {
    const permissions: any[] = [];

    // Always 'Member' role

    // if (!this.roleId().value) {
    //   return permissions;
    // }

    // const role = this.roles.find((r) => r.id === this.roleId().value);

    // if (role?.name === 'CustomerSpaceAdmin') {
    //   return permissions;
    // }

    if (this.appPermissionsForm.get('documents')!.value) {
      permissions.push(getPermissionForApp('DOCUMENTS', this.customerSpaceId));
    }

    if (this.appPermissionsForm.get('tasks')!.value) {
      permissions.push(getPermissionForApp('TASKS', this.customerSpaceId));
    }

    if (this.appPermissionsForm.get('payroll')!.value) {
      permissions.push(getPermissionForApp('PAYROLL', this.customerSpaceId));
    }

    if (this.appPermissionsForm.get('creditor')!.value) {
      permissions.push(getPermissionForApp('CREDITOR', this.customerSpaceId));
    }

    if (this.appPermissionsForm.get('projects')!.value) {
      permissions.push(getPermissionForApp('PROJECTS', this.customerSpaceId));
    }

    if (this.appPermissionsForm.get('financialStatementWorkings')!.value) {
      permissions.push(getPermissionForApp('FILE_PROCESS_FINANCIAL_STATEMENT_WORKINGS', this.customerSpaceId));
    }

    if (this.appPermissionsForm.get('taxDeclaration')!.value) {
      permissions.push(getPermissionForApp('FILE_PROCESS_TAX_DECLARATION', this.customerSpaceId));
    }

    if (this.appPermissionsForm.get('audit')!.value) {
      permissions.push(getPermissionForApp('FILE_PROCESS_AUDIT', this.customerSpaceId));
    }

    if (this.appPermissionsForm.get('links')!.value) {
      permissions.push(getPermissionForApp('LINKS', this.customerSpaceId));
    }

    if (this.appPermissionsForm.get('links')!.value && this.appPermissionsForm.get('linksAbacus')!.value) {
      permissions.push(getPermissionForApp('LINKS_ABACUS', this.customerSpaceId));
    }

    if (this.appPermissionsForm.get('links')!.value && this.appPermissionsForm.get('linksAbacusErp')!.value) {
      permissions.push(getPermissionForApp('LINKS_ABACUS_ERP', this.customerSpaceId));
    }

    if (this.appPermissionsForm.get('links')!.value && this.appPermissionsForm.get('linksAbacusObtErp')!.value) {
      permissions.push(getPermissionForApp('LINKS_ABACUS_OBT_ERP', this.customerSpaceId));
    }

    if (this.appPermissionsForm.get('links')!.value && this.appPermissionsForm.get('linksTicketPortal')!.value) {
      permissions.push(getPermissionForApp('LINKS_TICKET_PORTAL', this.customerSpaceId));
    }

    if (this.appPermissionsForm.get('links')!.value && this.appPermissionsForm.get('linksObtWebsite')!.value) {
      permissions.push(getPermissionForApp('LINKS_OBT_WEBSITE', this.customerSpaceId));
    }

    return permissions;
  }
}
